/** XeAgents.ai**/
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus, Phone, Trash2, Link as LinkIcon, Sparkles, PhoneIncoming, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

type IncomingConnection = {
  id: string;
  agentId: string;
  phoneNumberId: string;
  createdAt: string;
  isPlivo?: boolean;
  agent: {
    id: string;
    name: string;
    elevenLabsVoiceId: string | null;
    language: string | null;
    telephonyProvider?: string | null;
  };
  phoneNumber: {
    id: string;
    phoneNumber: string;
    friendlyName: string | null;
  };
};

type Agent = {
  id: string;
  name: string;
  type: 'incoming' | 'flow';
};

type PhoneNumber = {
  id: string;
  phoneNumber: string;
  friendlyName: string | null;
  isConflicted?: boolean;
  conflictReason?: string | null;
  conflictCampaignName?: string | null;
  conflictCampaignStatus?: string | null;
  isPlivo?: boolean;
};

type Stats = {
  totalConnections: number;
  availableNumbers: number;
  totalAgents: number;
};

type IncomingConnectionsResponse = {
  connections: IncomingConnection[];
  availablePhoneNumbers: PhoneNumber[];
  incomingAgents: Agent[];
  stats: Stats;
};

export default function IncomingConnectionsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConnection, setDeleteConnection] = useState<IncomingConnection | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedPhoneId, setSelectedPhoneId] = useState("");

  const { data, isLoading: connectionsLoading } = useQuery<IncomingConnectionsResponse>({
    queryKey: ["/api/incoming-connections"],
  });

  const connections = data?.connections || [];
  const availablePhoneNumbers = data?.availablePhoneNumbers || [];
  const agents = data?.incomingAgents || [];
  
  const totalNumbers = connections.length + availablePhoneNumbers.length;
  const stats = {
    totalConnections: data?.stats?.totalConnections || connections.length,
    availableNumbers: data?.stats?.availableNumbers || availablePhoneNumbers.length,
    totalAgents: data?.stats?.totalAgents || agents.length,
    totalNumbers,
    incomingAgents: agents.length,
  };

  const createMutation = useMutation({
    mutationFn: async (data: { agentId: string; phoneNumberId: string }) => {
      return apiRequest("POST", "/api/incoming-connections", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/incoming-connections"] });
      toast({
        title: t("incomingConnections.toast.created"),
        description: t("incomingConnections.toast.createdDesc"),
      });
      setCreateDialogOpen(false);
      setSelectedAgentId("");
      setSelectedPhoneId("");
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: t("incomingConnections.toast.createFailed"),
        description: error.message || t("incomingConnections.toast.createFailedDesc"),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/incoming-connections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/incoming-connections"] });
      toast({
        title: t("incomingConnections.toast.deleted"),
        description: t("incomingConnections.toast.deletedDesc"),
      });
      setDeleteConnection(null);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: t("incomingConnections.toast.deleteFailed"),
        description: error.message || t("incomingConnections.toast.deleteFailedDesc"),
      });
    },
  });

  const handleCreate = () => {
    if (!selectedAgentId || !selectedPhoneId) {
      toast({
        variant: "destructive",
        title: t("incomingConnections.toast.validationError"),
        description: t("incomingConnections.toast.validationErrorDesc"),
      });
      return;
    }

    createMutation.mutate({
      agentId: selectedAgentId,
      phoneNumberId: selectedPhoneId,
    });
  };

  const assignmentPercentage = stats.totalNumbers > 0 
    ? (stats.totalConnections / stats.totalNumbers) * 100 
    : 0;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-50 via-orange-100/50 to-yellow-50 dark:from-amber-950/40 dark:via-orange-900/30 dark:to-yellow-950/40 border border-amber-100 dark:border-amber-900/50 p-6 md:p-8">
        <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-700/20 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.5))]" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
              <PhoneIncoming className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="heading-incoming-connections">
                {t("incomingConnections.title")}
              </h1>
              <p className="text-muted-foreground mt-0.5">
                {t("incomingConnections.subtitle")}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            disabled={availablePhoneNumbers.length === 0 || agents.length === 0}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="button-create-connection"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t("incomingConnections.newConnection")}
          </Button>
        </div>

        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-amber-100/50 dark:border-amber-800/30">
            <div className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300" data-testid="stat-total-connections">{stats.totalConnections}</div>
            </div>
            <div className="text-amber-600/70 dark:text-amber-400/70 text-sm">{t("incomingConnections.stats.totalConnections")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-orange-100/50 dark:border-orange-800/30">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              <div className="text-2xl font-bold text-orange-700 dark:text-orange-300" data-testid="stat-total-numbers">{stats.totalNumbers}</div>
            </div>
            <div className="text-orange-600/70 dark:text-orange-400/70 text-sm">{t("incomingConnections.stats.phoneNumbers")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-yellow-100/50 dark:border-yellow-800/30">
            <div className="flex items-center gap-2">
              <Unlink className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300" data-testid="stat-available-numbers">{stats.availableNumbers}</div>
            </div>
            <div className="text-yellow-600/70 dark:text-yellow-400/70 text-sm">{t("incomingConnections.stats.availableNumbers")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-rose-100/50 dark:border-rose-800/30">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              <div className="text-2xl font-bold text-rose-700 dark:text-rose-300">{stats.incomingAgents}</div>
            </div>
            <div className="text-rose-600/70 dark:text-rose-400/70 text-sm">{t("incomingConnections.stats.incomingAgents")}</div>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("incomingConnections.card.title")}</CardTitle>
          <CardDescription>
            {t("incomingConnections.card.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connectionsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-12" data-testid="empty-state">
              <LinkIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("incomingConnections.empty.title")}</h3>
              <p className="text-sm text-muted-foreground mb-6">
                {t("incomingConnections.empty.description")}
              </p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                disabled={availablePhoneNumbers.length === 0 || agents.length === 0}
                data-testid="button-create-first-connection"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t("incomingConnections.empty.createFirst")}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {connections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover-elevate"
                  data-testid={`connection-card-${connection.id}`}
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-3">
                      <Badge variant="default" className="font-mono text-xs" data-testid={`badge-phone-${connection.id}`}>
                        {connection.phoneNumber.phoneNumber}
                      </Badge>
                      {connection.isPlivo && (
                        <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs">
                          Plivo
                        </Badge>
                      )}
                      <span className="text-muted-foreground">→</span>
                      <h3 className="font-semibold text-lg" data-testid={`text-agent-name-${connection.id}`}>
                        {connection.agent.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {connection.phoneNumber.friendlyName && (
                        <>
                          <span>{connection.phoneNumber.friendlyName}</span>
                          <span>•</span>
                        </>
                      )}
                      <span>{t("incomingConnections.connection.language")}: {connection.agent.language || 'en'}</span>
                      <span>•</span>
                      <span className="text-xs">
                        {t("incomingConnections.connection.created")} {formatDate(connection.createdAt, 'MMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteConnection(connection)}
                      data-testid={`button-delete-${connection.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent data-testid="dialog-create-connection">
          <DialogHeader>
            <DialogTitle>{t("incomingConnections.dialog.createTitle")}</DialogTitle>
            <DialogDescription>
              {t("incomingConnections.dialog.createDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="agent">
                {t("incomingConnections.form.agentLabel")} <span className="text-destructive">*</span>
              </Label>
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger id="agent" data-testid="select-agent">
                  <SelectValue placeholder={t("incomingConnections.form.agentPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {agents.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {t("incomingConnections.form.noAgents")}
                    </div>
                  ) : (
                    agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("incomingConnections.form.agentHint")}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                {t("incomingConnections.form.phoneLabel")} <span className="text-destructive">*</span>
              </Label>
              <Select value={selectedPhoneId} onValueChange={setSelectedPhoneId}>
                <SelectTrigger id="phone" data-testid="select-phone">
                  <SelectValue placeholder={t("incomingConnections.form.phonePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {availablePhoneNumbers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      {t("incomingConnections.form.noPhones")}
                    </div>
                  ) : (
                    availablePhoneNumbers.map((phone) => (
                      phone.isConflicted ? (
                        <TooltipProvider key={phone.id}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div 
                                className="relative flex w-full cursor-not-allowed select-none items-center rounded-sm py-1.5 px-2 text-sm outline-none opacity-50"
                                data-testid={`phone-option-conflicted-${phone.id}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono">{phone.phoneNumber}</span>
                                  {phone.friendlyName && (
                                    <span className="text-muted-foreground">({phone.friendlyName})</span>
                                  )}
                                  <span className="text-xs text-destructive font-medium">(In Use)</span>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p>{phone.conflictReason || "This phone is being used by an active campaign"}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <SelectItem key={phone.id} value={phone.id} data-testid={`phone-option-${phone.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{phone.phoneNumber}</span>
                            {phone.friendlyName && (
                              <span className="text-muted-foreground">({phone.friendlyName})</span>
                            )}
                            {phone.isPlivo && (
                              <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded">
                                Plivo
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      )
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("incomingConnections.form.phoneHint")}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setSelectedAgentId("");
                setSelectedPhoneId("");
              }}
              data-testid="button-cancel-create"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending || !selectedAgentId || !selectedPhoneId}
              data-testid="button-confirm-create"
            >
              {createMutation.isPending ? t("incomingConnections.actions.creating") : t("incomingConnections.actions.createConnection")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConnection} onOpenChange={() => setDeleteConnection(null)}>
        <AlertDialogContent data-testid="dialog-delete-connection">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("incomingConnections.dialog.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("incomingConnections.dialog.deleteDescription")}{" "}
              <span className="font-mono font-semibold">
                {deleteConnection?.phoneNumber.phoneNumber}
              </span>{" "}
              {t("incomingConnections.dialog.deleteDescriptionRoute")}{" "}
              <span className="font-semibold">{deleteConnection?.agent.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConnection && deleteMutation.mutate(deleteConnection.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? t("incomingConnections.actions.deleting") : t("incomingConnections.actions.deleteConnection")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
